import { useTranslation } from 'react-i18next';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/Shared/Button';
import {
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
} from '@/components/Shared/Table';
import { formatMoney } from '@/utils/money';

export interface PositionRow {
  id: string;
  title: string;
  description: string;
  unit: string;
  quantity: number;
  unitNetPrice: number;
  vatRate: number;
}

export function createEmptyPositionRow(defaults?: Partial<PositionRow>): PositionRow {
  return {
    id: crypto.randomUUID(),
    title: '',
    description: '',
    unit: 'Stk',
    quantity: 1,
    unitNetPrice: 0,
    vatRate: 20,
    ...defaults,
  };
}

const round2 = (value: number) => Math.round(value * 100) / 100;

export function calcPositionTotals(positions: PositionRow[]) {
  let net = 0;
  let vat = 0;
  for (const p of positions) {
    const lineNet = round2(p.quantity * p.unitNetPrice);
    const lineVat = round2(lineNet * (p.vatRate / 100));
    net += lineNet;
    vat += lineVat;
  }
  net = round2(net);
  vat = round2(vat);
  return { net, vat, gross: round2(net + vat) };
}

interface InvoicePositionEditorProps {
  positions: PositionRow[];
  onChange: (rows: PositionRow[]) => void;
  disabled?: boolean;
}

export function InvoicePositionEditor({
  positions,
  onChange,
  disabled,
}: InvoicePositionEditorProps) {
  const { t } = useTranslation(['invoices', 'common']);

  const updateRow = (id: string, patch: Partial<PositionRow>) => {
    onChange(positions.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  };

  const addRow = () => {
    onChange([...positions, createEmptyPositionRow()]);
  };

  const removeRow = (id: string) => {
    if (positions.length <= 1) return;
    onChange(positions.filter((p) => p.id !== id));
  };

  const totals = calcPositionTotals(positions);

  return (
    <div className="space-y-3">
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader className="w-[3rem]">#</TableHeader>
            <TableHeader>
              {t('invoices:manualCreate.fields.positionTitle')}
            </TableHeader>
            <TableHeader className="w-[6rem]">
              {t('invoices:manualCreate.fields.positionQuantity')}
            </TableHeader>
            <TableHeader className="w-[5rem]">
              {t('invoices:manualCreate.fields.positionUnit')}
            </TableHeader>
            <TableHeader className="w-[8rem] text-right">
              {t('invoices:manualCreate.fields.positionUnitNetPrice')}
            </TableHeader>
            <TableHeader className="w-[5rem]">
              {t('invoices:manualCreate.fields.positionVatRate')}
            </TableHeader>
            <TableHeader className="w-[7rem] text-right tabular-nums">
              {t('invoices:manualCreate.fields.positionNet')}
            </TableHeader>
            <TableHeader className="w-[3rem]">
              <span className="sr-only">{t('invoices:manualCreate.actions.removePosition')}</span>
            </TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {positions.map((p, idx) => {
            const lineNet = round2(p.quantity * p.unitNetPrice);
            return (
              <TableRow key={p.id}>
                <TableCell className="text-text-secondary">{idx + 1}</TableCell>
                <TableCell>
                  <div className="space-y-1">
                    <input
                      type="text"
                      value={p.title}
                      disabled={disabled}
                      onChange={(e) => updateRow(p.id, { title: e.target.value })}
                      placeholder={t('invoices:manualCreate.fields.positionTitle')}
                      className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                    />
                    <input
                      type="text"
                      value={p.description}
                      disabled={disabled}
                      onChange={(e) => updateRow(p.id, { description: e.target.value })}
                      placeholder={t('invoices:manualCreate.fields.positionDescription')}
                      className="w-full px-2 py-1 text-xs border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-700 text-text-secondary"
                    />
                  </div>
                </TableCell>
                <TableCell>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={p.quantity}
                    disabled={disabled}
                    onChange={(e) =>
                      updateRow(p.id, { quantity: Number(e.target.value) || 0 })
                    }
                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-right tabular-nums"
                  />
                </TableCell>
                <TableCell>
                  <input
                    type="text"
                    value={p.unit}
                    disabled={disabled}
                    onChange={(e) => updateRow(p.id, { unit: e.target.value })}
                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700"
                  />
                </TableCell>
                <TableCell>
                  <input
                    type="number"
                    min={0}
                    step="0.01"
                    value={p.unitNetPrice}
                    disabled={disabled}
                    onChange={(e) =>
                      updateRow(p.id, { unitNetPrice: Number(e.target.value) || 0 })
                    }
                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-right tabular-nums"
                  />
                </TableCell>
                <TableCell>
                  <input
                    type="number"
                    min={0}
                    max={100}
                    step="0.5"
                    value={p.vatRate}
                    disabled={disabled}
                    onChange={(e) =>
                      updateRow(p.id, { vatRate: Number(e.target.value) || 0 })
                    }
                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-right tabular-nums"
                  />
                </TableCell>
                <TableCell className="text-right tabular-nums font-medium">
                  {formatMoney(lineNet)}
                </TableCell>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => removeRow(p.id)}
                    disabled={disabled || positions.length <= 1}
                    className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/30 rounded disabled:opacity-30 disabled:cursor-not-allowed"
                    title={t('invoices:manualCreate.actions.removePosition')}
                    aria-label={t('invoices:manualCreate.actions.removePosition')}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>

      <div className="flex items-start justify-between gap-4">
        <Button
          variant="outline"
          size="sm"
          onClick={addRow}
          disabled={disabled}
          type="button"
        >
          {t('invoices:manualCreate.actions.addPosition')}
        </Button>

        <div className="text-sm space-y-1 min-w-[14rem]">
          <div className="flex justify-between text-text-secondary">
            <span>{t('invoices:manualCreate.totals.net')}</span>
            <span className="tabular-nums">{formatMoney(totals.net)}</span>
          </div>
          <div className="flex justify-between text-text-secondary">
            <span>{t('invoices:manualCreate.totals.vat')}</span>
            <span className="tabular-nums">{formatMoney(totals.vat)}</span>
          </div>
          <div className="flex justify-between font-semibold border-t border-gray-200 dark:border-gray-700 pt-1">
            <span>{t('invoices:manualCreate.totals.gross')}</span>
            <span className="tabular-nums">{formatMoney(totals.gross)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvoicePositionEditor;
